/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListUnproxiedMcpServersRequest,
  ListUnproxiedMcpServersSecurity,
} from "../models/operations/listunproxiedmcpservers.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildUnproxiedMcpServersQuery,
  prefetchUnproxiedMcpServers,
  queryKeyUnproxiedMcpServers,
  UnproxiedMcpServersQueryData,
} from "./unproxiedMcpServers.core.js";
export {
  buildUnproxiedMcpServersQuery,
  prefetchUnproxiedMcpServers,
  queryKeyUnproxiedMcpServers,
  type UnproxiedMcpServersQueryData,
};

export type UnproxiedMcpServersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listServers unproxiedMcp
 *
 * @remarks
 * List all unproxied MCP servers for a project
 */
export function useUnproxiedMcpServers(
  request?: ListUnproxiedMcpServersRequest | undefined,
  security?: ListUnproxiedMcpServersSecurity | undefined,
  options?: QueryHookOptions<
    UnproxiedMcpServersQueryData,
    UnproxiedMcpServersQueryError
  >,
): UseQueryResult<UnproxiedMcpServersQueryData, UnproxiedMcpServersQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildUnproxiedMcpServersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listServers unproxiedMcp
 *
 * @remarks
 * List all unproxied MCP servers for a project
 */
export function useUnproxiedMcpServersSuspense(
  request?: ListUnproxiedMcpServersRequest | undefined,
  security?: ListUnproxiedMcpServersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    UnproxiedMcpServersQueryData,
    UnproxiedMcpServersQueryError
  >,
): UseSuspenseQueryResult<
  UnproxiedMcpServersQueryData,
  UnproxiedMcpServersQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildUnproxiedMcpServersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setUnproxiedMcpServersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: UnproxiedMcpServersQueryData,
): UnproxiedMcpServersQueryData | undefined {
  const key = queryKeyUnproxiedMcpServers(...queryKeyBase);

  return client.setQueryData<UnproxiedMcpServersQueryData>(key, data);
}

export function invalidateUnproxiedMcpServers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "unproxiedMcp", "listServers", ...queryKeyBase],
  });
}

export function invalidateAllUnproxiedMcpServers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "unproxiedMcp", "listServers"],
  });
}
